import { APIError, getAPIBaseURL } from '@/lib/api/api';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import {
  EVENT_POSTER_CONSTANTS,
  EntryTypeCover,
  EntryTypeFree,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  EntryTypeTicketed,
  UploadPurposeEventPoster,
} from '@/lib/api/apiTypes';
import type {
  DiscoveryEventDetail,
  EntryType,
  EventImageAsset,
  EventImageInput,
  FeedEvent,
  EventRecord,
  ImageUploadWarning,
  PaginatedEventsResult,
  PresignedUpload,
  SignUploadRequest,
} from '@/lib/api/apiTypes';
import {
  getEntryTypeLabel,
  normalizeTagsToFetchedCatalog,
} from '@/features/events/eventTags';
import {
  filterSelectableEventTags,
} from '@/features/events/eventMobileHelpers';
import { locationStore } from '@/lib/location/location';
import { citywideEventKeys } from '@/features/events/feed/useCitywideEvents';
import { editableEventKeys } from '@/features/events/useEditableEvent';
import { eventDetailKeys, type EventDetailData } from '@/features/events/useEventDetail';
import type { QueryClient } from '@tanstack/react-query';
import type { ImagePickerAsset } from 'expo-image-picker';
import { DEFAULT_EVENT_DURATION_MINUTES } from './constants';
import type {
  AuthRequestClient,
  DraftPoster,
  EventDraft,
  EventEditorMode,
  EventEditorProps,
  FlyerDraftMetadata,
  FlyerOverrideField,
  FlyerReviewField,
  RemotePosterAsset,
} from './types';

export function getEditedEventFields(
  initialEvent: DiscoveryEventDetail,
  draft: EventDraft,
  startsAtISO: string,
  endsAtISO: string | null,
  doorsAtISO: string | null,
  lastEntryTimeISO: string | null
) {
  const fieldsChanged: string[] = [];

  if (draft.title.trim() !== initialEvent.title) fieldsChanged.push('title');
  if (draft.description.trim() !== initialEvent.description) fieldsChanged.push('description');
  if ((draft.shortDescription.trim() || null) !== (initialEvent.short_description ?? null)) {
    fieldsChanged.push('short_description');
  }
  if (draft.venueName.trim() !== initialEvent.venue_name) fieldsChanged.push('venue_name');
  if ((draft.venueId ?? null) !== (initialEvent.venue_id ?? null)) fieldsChanged.push('venue_id');
  if (draft.address.trim() !== initialEvent.address) fieldsChanged.push('address');
  if (draft.latitude !== initialEvent.coordinate.latitude || draft.longitude !== initialEvent.coordinate.longitude) {
    fieldsChanged.push('coordinate');
  }
  if (startsAtISO !== initialEvent.starts_at) fieldsChanged.push('starts_at');
  if (endsAtISO !== (initialEvent.ends_at ?? null)) fieldsChanged.push('ends_at');
  if (doorsAtISO !== (initialEvent.doors_at ?? null)) fieldsChanged.push('doors_at');
  const initialCapacity = initialEvent.capacity > 0 ? initialEvent.capacity : null;
  if (parseNullableInt(draft.capacity) !== initialCapacity) fieldsChanged.push('capacity');
  if (draft.ageRestriction !== initialEvent.age_restriction) fieldsChanged.push('age_restriction');
  if (draft.tags.join(',') !== initialEvent.tags.join(',')) fieldsChanged.push('tags');
  if (draft.attributes.join(',') !== initialEvent.attributes.join(',')) fieldsChanged.push('attributes');
  if (serializeDraftPosters(draft.posters) !== serializeEventAssets(getInitialPosterAssets(initialEvent))) {
    fieldsChanged.push('poster_images');
  }
  const normalizedEventURL = draft.entryType === EntryTypeTicketed ? null : draft.eventUrl.trim() || null;
  if (normalizedEventURL !== (initialEvent.event_url ?? null)) fieldsChanged.push('event_url');
  const normalizedTicketURL = draft.entryType === EntryTypeTicketed ? draft.ticketUrl.trim() || null : null;
  if (normalizedTicketURL !== (initialEvent.ticket_url ?? null)) fieldsChanged.push('ticket_url');
  if (draft.entryType !== initialEvent.entry_type) fieldsChanged.push('entry_type');
  if (parseNullableFloat(draft.coverPrice) !== (initialEvent.cover_price ?? null)) {
    fieldsChanged.push('cover_price');
  }
  if ((draft.coverNotes.trim() || null) !== (initialEvent.cover_notes ?? null)) fieldsChanged.push('cover_notes');
  const initialRSVPLimit =
    typeof initialEvent.rsvp_limit === 'number' && initialEvent.rsvp_limit > 0
      ? initialEvent.rsvp_limit
      : null;
  if (parseNullableInt(draft.rsvpLimit) !== initialRSVPLimit) fieldsChanged.push('rsvp_limit');
  if (lastEntryTimeISO !== (initialEvent.last_entry_time ?? null)) fieldsChanged.push('last_entry_time');
  if ((draft.language.trim() || 'en') !== (initialEvent.language || 'en')) fieldsChanged.push('language');
  if ((draft.eventFormat ?? null) !== (initialEvent.event_format ?? null)) fieldsChanged.push('event_format');
  if ((draft.dressCode.trim() || null) !== (initialEvent.dress_code ?? null)) fieldsChanged.push('dress_code');
  if ((draft.parkingInfo.trim() || null) !== (initialEvent.parking_info ?? null)) fieldsChanged.push('parking_info');
  if ((draft.presentedBy.trim() || null) !== (initialEvent.presented_by ?? null)) fieldsChanged.push('presented_by');
  if (draft.weatherDependent !== initialEvent.weather_dependent) fieldsChanged.push('weather_dependent');
  if (
    (draft.weatherDependent ? draft.rainPlan.trim() || null : null) !==
    (initialEvent.rain_plan ?? null)
  ) {
    fieldsChanged.push('rain_plan');
  }
  if ((draft.contactEmail.trim() || null) !== (initialEvent.contact_email ?? null)) fieldsChanged.push('contact_email');
  if ((draft.contactPhone.trim() || null) !== (initialEvent.contact_phone ?? null)) fieldsChanged.push('contact_phone');
  if ((draft.internalNotes.trim() || null) !== (initialEvent.internal_notes ?? null)) fieldsChanged.push('internal_notes');

  return fieldsChanged;
}

export function getFlyerFieldHint(
  flyerReview: FlyerDraftMetadata | null,
  ...fields: FlyerReviewField[]
) {
  if (!flyerReview) {
    return undefined;
  }

  if (fields.some((field) => flyerReview.needsReview.includes(field))) {
    return 'Needs review';
  }

  const relatedFields: Record<FlyerReviewField, FlyerOverrideField[]> = {
    title: ['title'],
    description: ['description'],
    venue: ['venueName'],
    address: ['address'],
    date: ['startsDate', 'endsDate', 'doorsDate'],
    time: ['startsTime', 'endsTime', 'doorsTime'],
    pricing: ['entryType', 'coverPrice', 'coverNotes'],
    tags: ['tags'],
    ageRestriction: ['ageRestriction'],
  };

  if (
    fields.some((field) =>
      relatedFields[field].some((relatedField) => flyerReview.appliedFields.includes(relatedField))
    )
  ) {
    return 'From flyer';
  }

  return undefined;
}

export function getEntryTypeGuidance(entryType: EntryType) {
  switch (entryType) {
    case EntryTypeCover:
      return 'Best for door charge nights. Add the cover price before publishing.';
    case EntryTypeTicketed:
      return 'Best when tickets live somewhere else. Add the starting price and ticket link.';
    case EntryTypeRSVP:
      return 'Best when you want a controlled list or capacity cap.';
    case EntryTypeFreeRSVP:
      return 'Best for free events where you still want a headcount.';
    default:
      return 'Best for open events with no price or RSVP gate.';
  }
}

export function getPostingSummary(draft: EventDraft, linkedVenueID: string | null) {
  const venueName = draft.venueName.trim();
  if (linkedVenueID) {
    return venueName ? `${venueName} venue team` : 'Venue team';
  }
  return venueName ? `Personal post at ${venueName}` : 'Personal post';
}

export function getEntrySummary(draft: EventDraft) {
  const label = getEntryTypeLabel(draft.entryType);
  if ((draft.entryType === EntryTypeCover || draft.entryType === EntryTypeTicketed) && draft.coverPrice.trim()) {
    return `${label} · $${draft.coverPrice.trim()}`;
  }
  if (draft.entryType === EntryTypeTicketed && !draft.ticketUrl.trim()) {
    return `${label} · ticket link needed`;
  }
  if (draft.entryType === EntryTypeCover && !draft.coverPrice.trim()) {
    return `${label} · cover price needed`;
  }
  if ((draft.entryType === EntryTypeRSVP || draft.entryType === EntryTypeFreeRSVP) && draft.rsvpLimit.trim()) {
    return `${label} · ${draft.rsvpLimit.trim()} guest cap`;
  }
  return label;
}

export function humanizeEventEditorSubmitError(error: unknown, mode: EventEditorMode) {
  if (error instanceof Error && error.message === 'Poster upload is still incomplete.') {
    return 'One of these posters needs to be re-added before you can save. Remove the broken poster and upload it again.';
  }

  if (error instanceof APIError) {
    return getAPIErrorRecoveryCopy(
      error,
      'event_editor',
      mode === 'edit'
        ? 'Unable to save changes right now. Try again in a minute.'
        : 'Unable to publish this event right now. Try again in a minute.'
    );
  }

  return mode === 'edit'
    ? 'Unable to save changes right now. Try again in a minute.'
    : 'Unable to publish this event right now. Try again in a minute.';
}

export function humanizeFlyerImportError(error: unknown) {
  if (error instanceof APIError) {
    return getAPIErrorRecoveryCopy(
      error,
      'flyer',
      'Unable to read flyer right now. You can still create the event manually.'
    );
  }

  return 'Unable to read flyer right now. You can still create the event manually.';
}

export function buildDateTime(dateValue: string, timeValue: string) {
  if (!dateValue.trim() || !timeValue.trim()) {
    return null;
  }
  const value = new Date(`${dateValue}T${timeValue}:00`);
  return Number.isNaN(value.getTime()) ? null : value;
}

export function parseNullableInt(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed === 0) {
    return null;
  }
  return parsed;
}

export function parseNullableFloat(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replace(/[^0-9.]/g, '');
  if (!normalized) {
    return null;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildUploadingDraftPoster(asset: ImagePickerAsset): DraftPoster {
  return {
    localID: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    imageID: null,
    uri: asset.uri,
    asset,
    objectKey: null,
    publicURL: null,
    focalPointX: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x,
    focalPointY: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y,
    focalScale: 1,
    width: asset.width ?? null,
    height: asset.height ?? null,
    status: 'uploading',
    progress: 0,
    uploadError: null,
    warnings: [],
  };
}

export function serializeDraftPosters(posters: DraftPoster[]) {
  // Dirty-checking includes crop state, because focal changes alter the submitted image payload.
  return posters
    .map((poster, position) => {
      const key = poster.objectKey ?? poster.uri;
      return `${position}:${key}:${poster.focalPointX.toFixed(3)}:${poster.focalPointY.toFixed(3)}:${poster.focalScale.toFixed(3)}`;
    })
    .join('|');
}

export function serializeEventAssets(posters: EventImageAsset[]) {
  return posters
    .slice()
    .sort((left, right) => left.position - right.position)
    .map((poster, position) =>
      `${position}:${poster.object_key}:${poster.focal_point_x.toFixed(3)}:${poster.focal_point_y.toFixed(3)}:${(poster.focal_scale ?? 1).toFixed(3)}`
    )
    .join('|');
}

function getInitialPosterAssets(initialEvent: DiscoveryEventDetail): EventImageAsset[] {
  if (Array.isArray(initialEvent.poster_images) && initialEvent.poster_images.length > 0) {
    return initialEvent.poster_images;
  }

  if (!initialEvent.poster_url) {
    return [];
  }

  // Old events may only have poster_url; synthesize one asset so edit mode still shows the poster.
  return [
    {
      id: `legacy-${initialEvent.id}`,
      object_key: '',
      public_url: initialEvent.poster_url,
      position: 0,
      focal_point_x: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x,
      focal_point_y: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y,
      focal_scale: 1,
    },
  ];
}

export function clampUnit(value: number) {
  return Math.min(1, Math.max(0, value));
}

export function clampInteger(value: number, min: number, max: number) {
  'worklet';
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function clampNumber(value: number, min: number, max: number) {
  'worklet';
  return Math.min(max, Math.max(min, value));
}

export function clampPosterImagePan({
  imageRect,
  scale,
  panX,
  panY,
  frameRect,
}: {
  imageRect: { x: number; y: number; width: number; height: number };
  scale: number;
  panX: number;
  panY: number;
  frameRect: { width: number; height: number; left: number; top: number } | null;
}) {
  'worklet';

  if (!frameRect || !imageRect.width || !imageRect.height) {
    return { x: panX, y: panY };
  }

  const scaledWidth = imageRect.width * scale;
  const scaledHeight = imageRect.height * scale;
  const leftOffset = (1 - scale) * imageRect.width / 2;
  const topOffset = (1 - scale) * imageRect.height / 2;

  const minPanX = frameRect.left - imageRect.x - leftOffset - scaledWidth + frameRect.width;
  const maxPanX = frameRect.left - imageRect.x - leftOffset;
  const minPanY = frameRect.top - imageRect.y - topOffset - scaledHeight + frameRect.height;
  const maxPanY = frameRect.top - imageRect.y - topOffset;

  return {
    x: clampNumber(panX, minPanX, maxPanX),
    y: clampNumber(panY, minPanY, maxPanY),
  };
}

export function getPosterImageScreenPoint({
  imageRect,
  scale,
  panX,
  panY,
  focalPointX,
  focalPointY,
}: {
  imageRect: { x: number; y: number; width: number; height: number };
  scale: number;
  panX: number;
  panY: number;
  focalPointX: number;
  focalPointY: number;
}) {
  'worklet';

  return {
    x: imageRect.x + imageRect.width / 2 + (focalPointX * imageRect.width - imageRect.width / 2) * scale + panX,
    y: imageRect.y + imageRect.height / 2 + (focalPointY * imageRect.height - imageRect.height / 2) * scale + panY,
  };
}

export function getContainedImageRect(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number | null,
  imageHeight: number | null
) {
  if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) {
    return null;
  }

  const imageAspect = imageWidth / imageHeight;
  const containerAspect = containerWidth / containerHeight;

  if (imageAspect > containerAspect) {
    const width = containerWidth;
    const height = width / imageAspect;
    return {
      x: 0,
      y: (containerHeight - height) / 2,
      width,
      height,
    };
  }

  const height = containerHeight;
  const width = height * imageAspect;
  return {
    x: (containerWidth - width) / 2,
    y: 0,
    width,
    height,
  };
}

export function humanizePosterUploadError(error: unknown) {
  if (error instanceof APIError) {
    return getAPIErrorRecoveryCopy(error, 'poster_upload', 'We couldn’t upload that poster right now. Try again.');
  }

  if (error instanceof Error) {
    if (error.message === 'Photo library access is required to select a flyer.') {
      return error.message;
    }
    if (error.message) {
      return error.message;
    }
  }

  return 'We couldn’t upload that poster right now. Try again.';
}

export function parseFlyerPriceValue(value: string) {
  const parsed = parseNullableFloat(value);
  return parsed === null ? null : String(parsed);
}

export function hasPartialDateTime(dateValue: string, timeValue: string) {
  return Boolean(dateValue.trim()) !== Boolean(timeValue.trim());
}

export function parseExtractedDateTime(value?: string | null) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isDefaultScheduleValue(
  draft: EventDraft,
  scheduleDefaults: ReturnType<typeof getInitialSchedule>,
  field: 'startsDate' | 'startsTime' | 'endsDate' | 'endsTime'
) {
  return draft[field] === scheduleDefaults[field];
}

export function mergeDraftWithExtracted({
  draft,
  extracted,
  scheduleDefaults,
}: {
  draft: EventDraft;
  extracted: import('@/features/events/editor/flyerCurator').ExtractedEvent;
  scheduleDefaults: ReturnType<typeof getInitialSchedule>;
}) {
  // Flyer import fills blanks/default schedule values only, so it never silently overwrites user edits.
  const patch: Partial<EventDraft> = {};
  const appliedFields: FlyerOverrideField[] = [];
  const importedValues: Partial<Record<FlyerOverrideField, string>> = {};
  const needsReview: FlyerReviewField[] = [];

  if (extracted.title?.trim() && !draft.title.trim()) {
    patch.title = extracted.title.trim();
    appliedFields.push('title');
    importedValues.title = extracted.title.trim();
    if (shouldMarkForReview(extracted.title_confidence)) {
      needsReview.push('title');
    }
  }

  if (extracted.description?.trim() && !draft.description.trim()) {
    patch.description = extracted.description.trim();
    appliedFields.push('description');
    importedValues.description = extracted.description.trim();
    if (!needsReview.includes('description')) {
      needsReview.push('description');
    }
  }

  if (extracted.venue_name?.trim() && !draft.venueName.trim()) {
    patch.venueName = extracted.venue_name.trim();
    patch.venueId = null;
    appliedFields.push('venueName');
    importedValues.venueName = extracted.venue_name.trim();
    if (shouldMarkForReview(extracted.venue_confidence)) {
      needsReview.push('venue');
    }
  }

  if (extracted.address?.trim() && !draft.address.trim()) {
    patch.address = extracted.address.trim();
    patch.latitude = 0;
    patch.longitude = 0;
    appliedFields.push('address');
    importedValues.address = extracted.address.trim();
    if (!needsReview.includes('address')) {
      needsReview.push('address');
    }
  }

  const startsAt = parseExtractedDateTime(extracted.starts_at);
  if (
    startsAt &&
    isDefaultScheduleValue(draft, scheduleDefaults, 'startsDate') &&
    isDefaultScheduleValue(draft, scheduleDefaults, 'startsTime')
  ) {
    patch.startsDate = formatDateInput(startsAt);
    patch.startsTime = formatTimeInput(startsAt);
    appliedFields.push('startsDate', 'startsTime');
    importedValues.startsDate = patch.startsDate;
    importedValues.startsTime = patch.startsTime;
    if (shouldMarkForReview(extracted.date_confidence)) {
      needsReview.push('date');
    }
    if (shouldMarkForReview(extracted.time_confidence)) {
      needsReview.push('time');
    }
  }

  const endsAt = parseExtractedDateTime(extracted.ends_at);
  if (
    endsAt &&
    isDefaultScheduleValue(draft, scheduleDefaults, 'endsDate') &&
    isDefaultScheduleValue(draft, scheduleDefaults, 'endsTime')
  ) {
    patch.endsDate = formatDateInput(endsAt);
    patch.endsTime = formatTimeInput(endsAt);
    appliedFields.push('endsDate', 'endsTime');
    importedValues.endsDate = patch.endsDate;
    importedValues.endsTime = patch.endsTime;
    if (shouldMarkForReview(extracted.date_confidence) && !needsReview.includes('date')) {
      needsReview.push('date');
    }
    if (shouldMarkForReview(extracted.time_confidence) && !needsReview.includes('time')) {
      needsReview.push('time');
    }
  }

  const doorsAt = parseExtractedDateTime(extracted.doors_at);
  if (doorsAt && !draft.doorsDate.trim() && !draft.doorsTime.trim()) {
    patch.doorsDate = formatDateInput(doorsAt);
    patch.doorsTime = formatTimeInput(doorsAt);
    appliedFields.push('doorsDate', 'doorsTime');
    importedValues.doorsDate = patch.doorsDate;
    importedValues.doorsTime = patch.doorsTime;
    if (shouldMarkForReview(extracted.date_confidence) && !needsReview.includes('date')) {
      needsReview.push('date');
    }
    if (shouldMarkForReview(extracted.time_confidence) && !needsReview.includes('time')) {
      needsReview.push('time');
    }
  }

  if (extracted.age_restriction && draft.ageRestriction === 'all_ages') {
    patch.ageRestriction = extracted.age_restriction;
    appliedFields.push('ageRestriction');
    importedValues.ageRestriction = extracted.age_restriction;
    if (!needsReview.includes('ageRestriction')) {
      needsReview.push('ageRestriction');
    }
  }

  if (extracted.tags?.length && !draft.tags.length) {
    const nextTags = normalizeFlyerTags(extracted.tags);
    if (nextTags.length) {
      patch.tags = nextTags;
      appliedFields.push('tags');
      importedValues.tags = nextTags.join(',');
      if (shouldMarkForReview(extracted.tags_confidence) && !needsReview.includes('tags')) {
        needsReview.push('tags');
      }
    }
  }

  if (extracted.event_url?.trim() && !draft.eventUrl.trim()) {
    patch.eventUrl = extracted.event_url.trim();
    appliedFields.push('eventUrl');
    importedValues.eventUrl = extracted.event_url.trim();
  }

  if (extracted.ticket_url?.trim() && !draft.ticketUrl.trim()) {
    patch.ticketUrl = extracted.ticket_url.trim();
    appliedFields.push('ticketUrl');
    importedValues.ticketUrl = extracted.ticket_url.trim();
    if (draft.entryType === EntryTypeFree) {
      patch.entryType = EntryTypeTicketed;
      appliedFields.push('entryType');
      importedValues.entryType = EntryTypeTicketed;
    }
  }

  if (typeof extracted.capacity === 'number' && !draft.capacity.trim()) {
    patch.capacity = String(extracted.capacity);
    appliedFields.push('capacity');
    importedValues.capacity = patch.capacity;
  }

  if (
    typeof extracted.is_free === 'boolean' &&
    draft.entryType === EntryTypeFree &&
    !draft.coverPrice.trim() &&
    !draft.coverNotes.trim()
  ) {
    patch.entryType = extracted.is_free ? EntryTypeFree : EntryTypeCover;
    appliedFields.push('entryType');
    importedValues.entryType = patch.entryType;
  }

  if (extracted.price?.trim() && !draft.coverPrice.trim() && !draft.coverNotes.trim()) {
    const parsedCoverPrice = parseFlyerPriceValue(extracted.price);
    if (parsedCoverPrice) {
      patch.coverPrice = parsedCoverPrice;
      appliedFields.push('coverPrice');
      importedValues.coverPrice = parsedCoverPrice;
    }
    if (!parsedCoverPrice || extracted.price.trim() !== parsedCoverPrice) {
      patch.coverNotes = extracted.price.trim();
      appliedFields.push('coverNotes');
      importedValues.coverNotes = extracted.price.trim();
    }
    if (draft.entryType === EntryTypeFree) {
      patch.entryType = EntryTypeCover;
      appliedFields.push('entryType');
      importedValues.entryType = EntryTypeCover;
    }
    if (shouldMarkForReview(extracted.price_confidence) && !needsReview.includes('pricing')) {
      needsReview.push('pricing');
    }
  }

  return {
    patch,
    appliedFields,
    importedValues,
    needsReview,
  };
}

export function normalizeFlyerTags(tags: string[]) {
  return normalizeTagsToFetchedCatalog(tags);
}

export function shouldMarkForReview(confidence?: number | null) {
  return typeof confidence === 'number' && confidence < 70;
}

export function mapDraftFieldToFlyerField(field: keyof EventDraft): FlyerOverrideField | null {
  switch (field) {
    case 'title':
    case 'description':
    case 'venueName':
    case 'address':
    case 'doorsDate':
    case 'doorsTime':
    case 'startsDate':
    case 'startsTime':
    case 'endsDate':
    case 'endsTime':
    case 'ageRestriction':
    case 'eventUrl':
    case 'ticketUrl':
    case 'entryType':
    case 'coverPrice':
    case 'coverNotes':
    case 'capacity':
    case 'tags':
      return field;
    default:
      return null;
  }
}

export function normalizeFlyerComparableValue<K extends keyof EventDraft>(field: K, value: EventDraft[K]) {
  if (field === 'tags' && Array.isArray(value)) {
    return value.join(',');
  }

  if (typeof value === 'string') {
    return value;
  }

  return null;
}

export function getPickerMode(field: import('./types').PickerField) {
  return field === 'startsDate' || field === 'endsDate' || field === 'doorsDate' ? 'date' : 'time';
}

export function getPickerValue(field: import('./types').PickerField, draft: EventDraft) {
  const fallback = new Date();
  const startsAt = buildDateTime(draft.startsDate, draft.startsTime);

  if (field === 'startsDate' || field === 'startsTime') {
    return startsAt ?? fallback;
  }

  if (field === 'endsDate' || field === 'endsTime') {
    return (
      buildDateTime(draft.endsDate, draft.endsTime) ??
      (startsAt
        ? new Date(startsAt.getTime() + DEFAULT_EVENT_DURATION_MINUTES * 60 * 1000)
        : fallback)
    );
  }

  if (field === 'lastEntryTime') {
    return buildDateTime(draft.startsDate, draft.lastEntryTime) ?? startsAt ?? fallback;
  }

  return buildDateTime(draft.doorsDate, draft.doorsTime) ?? startsAt ?? fallback;
}

export function applyPickerValue(
  field: import('./types').PickerField,
  nextValue: Date,
  draft: EventDraft
): Partial<EventDraft> {
  const patch: Partial<EventDraft> = (() => {
    switch (field) {
      case 'startsDate':
        return { startsDate: formatDateInput(nextValue) };
      case 'startsTime':
        return { startsTime: formatTimeInput(nextValue) };
      case 'endsDate':
        return { endsDate: formatDateInput(nextValue) };
      case 'endsTime':
        return { endsTime: formatTimeInput(nextValue) };
      case 'doorsDate':
        return { doorsDate: formatDateInput(nextValue) };
      case 'doorsTime':
        return { doorsTime: formatTimeInput(nextValue) };
      case 'lastEntryTime':
        return { lastEntryTime: formatTimeInput(nextValue) };
    }
  })();

  if (field === 'endsDate' || field === 'endsTime' || field === 'doorsDate' || field === 'doorsTime') {
    return patch;
  }

  const currentStartsAt = buildDateTime(draft.startsDate, draft.startsTime);
  const currentEndsAt = buildDateTime(draft.endsDate, draft.endsTime);
  const nextDraft = { ...draft, ...patch };
  const nextStartsAt = buildDateTime(nextDraft.startsDate, nextDraft.startsTime);
  const nextEndsAt = buildDateTime(nextDraft.endsDate, nextDraft.endsTime);

  if (!nextStartsAt || !nextEndsAt || nextEndsAt > nextStartsAt) {
    return patch;
  }

  const durationMinutes = getDurationMinutes(currentStartsAt, currentEndsAt);
  const adjustedEndsAt = new Date(nextStartsAt.getTime() + durationMinutes * 60 * 1000);

  return {
    ...patch,
    endsDate: formatDateInput(adjustedEndsAt),
    endsTime: formatTimeInput(adjustedEndsAt),
  };
}

export function getInitialSchedule() {
  const now = new Date();
  const rounded = new Date(now);
  rounded.setMinutes(Math.ceil(now.getMinutes() / 30) * 30, 0, 0);
  if (rounded.getMinutes() === 60) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  }

  return {
    startsDate: formatDateInput(rounded),
    startsTime: formatTimeInput(rounded),
    endsDate: '',
    endsTime: '',
  };
}

export function formatDateInput(value: Date) {
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-');
}

export function formatTimeInput(value: Date) {
  return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
}

export function formatDate(value: string) {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
}

export function formatTime(value: string) {
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return value;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const normalizedHour = hours % 12 || 12;
  const normalizedMinutes = minutes === 0 ? '' : `:${String(minutes).padStart(2, '0')}`;

  return `${normalizedHour}${normalizedMinutes} ${period}`;
}

export function getDurationHint(startsAt: Date | null, endsAt: Date | null) {
  if (!startsAt || !endsAt) {
    return null;
  }

  const durationMinutes = Math.round((endsAt.getTime() - startsAt.getTime()) / 60000);
  if (durationMinutes <= 0) {
    return { label: 'End time must be after start time.', tone: 'error' as const };
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return {
      label: `${hours} hour${hours === 1 ? '' : 's'} ${minutes} min`,
      tone: 'default' as const,
    };
  }

  if (hours > 0) {
    return {
      label: `${hours} hour${hours === 1 ? '' : 's'}`,
      tone: 'default' as const,
    };
  }

  return { label: `${minutes} min`, tone: 'default' as const };
}

export function getDurationMinutes(startsAt: Date | null, endsAt: Date | null) {
  if (!startsAt || !endsAt) {
    return DEFAULT_EVENT_DURATION_MINUTES;
  }

  const durationMinutes = Math.round((endsAt.getTime() - startsAt.getTime()) / 60000);
  if (durationMinutes <= 0) {
    return DEFAULT_EVENT_DURATION_MINUTES;
  }

  return durationMinutes;
}

export function hasCoordinate(latitude: number, longitude: number) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }
  return !(latitude === 0 && longitude === 0);
}

export function getEventSaveRequest(mode: EventEditorMode, eventID?: string) {
  return {
    path: mode === 'edit' && eventID ? `/events/${eventID}` : '/events',
    method: mode === 'edit' ? 'PATCH' : 'POST',
  } as const;
}

export function syncSavedEventCaches(
  queryClient: QueryClient,
  event: EventRecord
) {
  // Entry edits are visible in editor, detail, and citywide caches before the next network refetch.
  const applyEventSummaryPatch = <T extends {
    event_url?: string | null;
    ticket_url?: string | null;
    entry_type?: EntryType | null;
    cover_price?: number | null;
    cover_notes?: string | null;
  }>(item: T): T => ({
    ...item,
    event_url: event.entry_type === EntryTypeTicketed ? null : (event.event_url ?? null),
    ticket_url: event.entry_type === EntryTypeTicketed ? event.ticket_url ?? null : null,
    entry_type: event.entry_type,
    cover_price: event.cover_price ?? null,
    cover_notes: event.cover_notes ?? null,
  });

  queryClient.setQueriesData<DiscoveryEventDetail>(
    { queryKey: editableEventKeys.all },
    (current: DiscoveryEventDetail | undefined) =>
      current && current.id === event.id
        ? applyEventSummaryPatch(current)
        : current
  );

  queryClient.setQueriesData<EventDetailData>(
    { queryKey: eventDetailKeys.byId(event.id) },
    (current: EventDetailData | undefined) =>
      current
        ? {
            ...current,
            event: applyEventSummaryPatch(current.event),
          }
        : current
  );

  queryClient.setQueriesData<PaginatedEventsResult | FeedEvent[]>(
    { queryKey: citywideEventKeys.all },
    (current: PaginatedEventsResult | FeedEvent[] | undefined) => {
      if (!current) {
        return current;
      }

      if (Array.isArray(current)) {
        return current.map((item) => (item.id === event.id ? applyEventSummaryPatch(item) : item));
      }

      return {
        ...current,
        events: current.events.map((item) =>
          item.id === event.id ? applyEventSummaryPatch(item) : item
        ),
      };
    }
  );
}

export function buildEventDraft({
  mode,
  initialEvent,
  scheduleDefaults,
  createPrefill,
}: {
  mode: EventEditorMode;
  initialEvent?: DiscoveryEventDetail | null;
  scheduleDefaults: ReturnType<typeof getInitialSchedule>;
  createPrefill?: EventEditorProps['createPrefill'];
}): EventDraft {
  if (mode === 'edit' && initialEvent) {
    const initialPosters = getInitialPosterAssets(initialEvent);
    return {
      title: initialEvent.title,
      description: initialEvent.description,
      shortDescription: initialEvent.short_description ?? '',
      capacity: initialEvent.capacity > 0 ? String(initialEvent.capacity) : '',
      ageRestriction: initialEvent.age_restriction,
      tags: filterSelectableEventTags(initialEvent.tags),
      attributes: initialEvent.attributes ?? [],
      venueName: initialEvent.venue_name,
      venueId: initialEvent.venue_id ?? null,
      address: initialEvent.address,
      latitude: initialEvent.coordinate.latitude,
      longitude: initialEvent.coordinate.longitude,
      startsDate: formatDateInput(new Date(initialEvent.starts_at)),
      startsTime: formatTimeInput(new Date(initialEvent.starts_at)),
      endsDate: initialEvent.ends_at ? formatDateInput(new Date(initialEvent.ends_at)) : '',
      endsTime: initialEvent.ends_at ? formatTimeInput(new Date(initialEvent.ends_at)) : '',
      doorsDate: initialEvent.doors_at ? formatDateInput(new Date(initialEvent.doors_at)) : '',
      doorsTime: initialEvent.doors_at ? formatTimeInput(new Date(initialEvent.doors_at)) : '',
      posters: initialPosters
        .slice()
        .sort((left, right) => left.position - right.position)
        .map(toRemotePosterDraft),
      eventUrl: initialEvent.event_url ?? '',
      ticketUrl: initialEvent.ticket_url ?? '',
      entryType: initialEvent.entry_type ?? EntryTypeFree,
      coverPrice:
        typeof initialEvent.cover_price === 'number' ? String(initialEvent.cover_price) : '',
      coverNotes: initialEvent.cover_notes ?? '',
      rsvpLimit:
        typeof initialEvent.rsvp_limit === 'number' && initialEvent.rsvp_limit > 0
          ? String(initialEvent.rsvp_limit)
          : '',
      lastEntryTime: initialEvent.last_entry_time ? formatTimeInput(new Date(initialEvent.last_entry_time)) : '',
      language: initialEvent.language || 'en',
      eventFormat: initialEvent.event_format ?? null,
      dressCode: initialEvent.dress_code ?? '',
      parkingInfo: initialEvent.parking_info ?? '',
      presentedBy: initialEvent.presented_by ?? '',
      weatherDependent: initialEvent.weather_dependent ?? false,
      rainPlan: initialEvent.rain_plan ?? '',
      contactEmail: initialEvent.contact_email ?? '',
      contactPhone: initialEvent.contact_phone ?? '',
      internalNotes: initialEvent.internal_notes ?? '',
      seriesId: initialEvent.series_id ?? '',
    };
  }

  return {
    title: '',
    description: '',
    shortDescription: '',
    capacity: '',
    ageRestriction: 'all_ages',
    tags: [],
    attributes: [],
    venueName: createPrefill?.venueName?.trim() ?? '',
    venueId: createPrefill?.venueId ?? null,
    address: createPrefill?.address?.trim() ?? '',
    latitude: createPrefill?.latitude ?? locationStore.getState().coords.latitude,
    longitude: createPrefill?.longitude ?? locationStore.getState().coords.longitude,
    startsDate: scheduleDefaults.startsDate,
    startsTime: scheduleDefaults.startsTime,
    endsDate: scheduleDefaults.endsDate,
    endsTime: scheduleDefaults.endsTime,
    doorsDate: '',
    doorsTime: '',
    posters: [],
    eventUrl: '',
    ticketUrl: '',
    entryType: EntryTypeFree,
    coverPrice: '',
    coverNotes: '',
    rsvpLimit: '',
    lastEntryTime: '',
    language: 'en',
    eventFormat: null,
    dressCode: '',
    parkingInfo: '',
    presentedBy: '',
    weatherDependent: false,
    rainPlan: '',
    contactEmail: '',
    contactPhone: '',
    internalNotes: '',
    seriesId: '',
  };
}

function toRemotePosterDraft(asset: RemotePosterAsset): DraftPoster {
  return {
    localID: `remote-${asset.id}`,
    imageID: asset.id,
    uri: asset.public_url,
    objectKey: asset.object_key,
    publicURL: asset.public_url,
    focalPointX: asset.focal_point_x,
    focalPointY: asset.focal_point_y,
    focalScale: asset.focal_scale ?? 1,
    width: null,
    height: null,
    status: 'uploaded',
    progress: 1,
    uploadError: null,
    warnings: [],
  };
}

export async function toEventImageInput(
  auth: AuthRequestClient,
  poster: DraftPoster,
  position: number
): Promise<EventImageInput> {
  if (poster.objectKey && poster.publicURL) {
    return {
      object_key: poster.objectKey,
      public_url: poster.publicURL,
      position,
      focal_point_x: poster.focalPointX,
      focal_point_y: poster.focalPointY,
      focal_scale: poster.focalScale,
    };
  }

  if (!poster.asset) {
    throw new Error('Poster upload is still incomplete.');
  }

  // Existing remote posters reuse their object key; only new local assets need a signed upload.
  return uploadPoster(auth, poster.asset, position);
}

async function uploadPoster(
  auth: AuthRequestClient,
  asset: ImagePickerAsset,
  position: number
): Promise<EventImageInput> {
  const fileName = asset.fileName ?? `poster-${Date.now()}-${position}.jpg`;
  const contentType = asset.mimeType ?? 'image/jpeg';
  const body = await assetToUploadBody(asset);
  const signPayload: SignUploadRequest = {
    file_name: fileName,
    content_type: contentType,
    purpose: UploadPurposeEventPoster,
    file_size_bytes: body.size,
    image_width: asset.width ?? undefined,
    image_height: asset.height ?? undefined,
  };
  const upload = await auth.request<PresignedUpload>('/uploads/sign', {
    method: 'POST',
    body: JSON.stringify(signPayload),
  });

  const response = await fetch(upload.url, {
    method: upload.method,
    headers: upload.headers,
    body,
  });
  if (!response.ok) {
    throw new Error('Poster upload failed.');
  }

  return {
    object_key: upload.object_key,
    public_url: upload.public_url,
    position,
    focal_point_x: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x,
    focal_point_y: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y,
    focal_scale: 1,
  };
}

export async function uploadPosterImmediately({
  accessToken,
  asset,
  onProgress,
  onReady,
}: {
  accessToken: string;
  asset: ImagePickerAsset;
  onProgress: (progress: number) => void;
  onReady: (request: XMLHttpRequest) => void;
}): Promise<{
  url: string;
  object_key: string;
  warnings?: ImageUploadWarning[];
}> {
  return await new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('poster', {
      uri: asset.uri,
      type: asset.mimeType ?? 'image/jpeg',
      name: asset.fileName ?? 'poster.jpg',
    } as any);

    request.open('POST', `${getAPIBaseURL()}/uploads/poster`);
    request.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    request.responseType = 'text';
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable || !event.total) {
        return;
      }
      onProgress(event.loaded / event.total);
    };
    request.onerror = () => reject(new Error('Unable to upload that poster right now.'));
    request.onabort = () => reject(new Error('upload_aborted'));
    request.onload = () => {
      const responseText =
        typeof request.response === 'string' ? request.response : request.responseText;
      const payload = responseText ? JSON.parse(responseText) : null;
      if (request.status < 200 || request.status >= 300) {
        // Preserve structured upload errors so the editor can show friendly, field-aware copy.
        reject(
          new APIError(payload?.error ?? 'Poster upload failed.', request.status, payload?.code, {
            fieldErrors: Array.isArray(payload?.field_errors) ? payload.field_errors : [],
            requestID: typeof payload?.request_id === 'string' ? payload.request_id : undefined,
          })
        );
        return;
      }
      resolve(payload);
    };

    onReady(request);
    request.send(formData);
  });
}

async function assetToUploadBody(asset: ImagePickerAsset) {
  if (asset.file) {
    return asset.file;
  }

  const response = await fetch(asset.uri);
  if (!response.ok) {
    throw new Error('Unable to read the selected image.');
  }
  return await response.blob();
}
